import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, LogIn, ArrowRight, ShieldCheck } from "lucide-react";
import { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function BrandedPortalLogin() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, setUser } = useAuth();
  const inviteToken = params.get("invite");

  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [invite, setInvite] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        if (inviteToken) {
          const { data } = await axios.get(`${API}/public/invite/${inviteToken}`);
          setInvite(data); setBrand(data.company); setEmail(data.email); setName(data.name || "");
        } else {
          const { data } = await axios.get(`${API}/public/company/${slug}`);
          setBrand(data);
        }
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    })();
  }, [slug, inviteToken]);

  const doLogin = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) navigate("/portal"); else setError(r.error || "Identifiants incorrects");
  };

  const doActivate = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/public/invite/${inviteToken}/accept`, { name, password });
      localStorage.setItem("fd_token", data.token);
      setUser(data.user);
      navigate("/portal");
    } catch (err) { setError(err.response?.data?.detail || "Activation impossible"); }
    finally { setBusy(false); }
  };

  if (loading) return <Center><Loader2 className="animate-spin text-zinc-400" size={30} /></Center>;
  if (notFound) return <Center><p className="text-zinc-400 text-sm">Espace client introuvable.</p></Center>;

  const color = brand?.theme || "#002FA7";
  const initial = (brand?.name || "?").charAt(0).toUpperCase();

  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-4 py-10" style={{ background: `radial-gradient(1200px 500px at 50% -10%, ${color}22, transparent), #0b0b0f` }}>
      <div className="w-full max-w-sm" data-testid="branded-login">
        <div className="flex flex-col items-center mb-6">
          <div className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden ring-2 shadow-lg" style={{ ringColor: color, background: "#fff", boxShadow: `0 0 0 3px ${color}33` }}>
            {brand?.logo_url ? <img src={brand.logo_url} alt={brand.name} className="w-full h-full object-cover" /> : <span className="text-2xl font-black" style={{ color }}>{initial}</span>}
          </div>
          <h1 className="mt-4 font-cabinet font-black text-2xl text-white text-center">{brand?.name}</h1>
          <p className="text-zinc-400 text-sm mt-1">{invite ? "Activez votre accès client" : "Espace client"}</p>
        </div>

        <div className="bg-card border border-white/10 rounded-2xl p-6 shadow-xl">
          {invite ? (
            <form onSubmit={doActivate} className="space-y-3" data-testid="activation-form">
              <Field label="Email"><input value={email} disabled className="finp opacity-60" data-testid="activate-email" /></Field>
              <Field label="Votre nom"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom complet" className="finp" data-testid="activate-name" /></Field>
              <Field label="Créer un mot de passe"><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6 caractères minimum" className="finp" data-testid="activate-password" required /></Field>
              {error && <p className="text-signal text-xs" data-testid="branded-error">{error}</p>}
              <button disabled={busy || password.length < 6} data-testid="activate-submit" className="w-full h-11 rounded-lg font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: color }}>
                {busy ? <Loader2 size={17} className="animate-spin" /> : <ArrowRight size={17} />} Activer mon accès
              </button>
            </form>
          ) : (
            <form onSubmit={doLogin} className="space-y-3" data-testid="branded-login-form">
              <Field label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.com" className="finp" data-testid="branded-email" required /></Field>
              <Field label="Mot de passe"><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="finp" data-testid="branded-password" required /></Field>
              {error && <p className="text-signal text-xs" data-testid="branded-error">{error}</p>}
              <button disabled={busy} data-testid="branded-submit" className="w-full h-11 rounded-lg font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: color }}>
                {busy ? <Loader2 size={17} className="animate-spin" /> : <LogIn size={17} />} Se connecter
              </button>
            </form>
          )}
        </div>
        <p className="flex items-center justify-center gap-1.5 text-xs text-zinc-500 mt-5"><ShieldCheck size={13} /> Espace sécurisé propulsé par FlowDesk</p>
      </div>
      <style>{`.finp{width:100%;background:hsl(var(--background));border:1px solid hsl(var(--border));border-radius:9px;padding:10px 12px;font-size:14px;color:#fff;outline:none}.finp:focus{box-shadow:0 0 0 2px ${color}44}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-[10px] font-medium uppercase tracking-widest text-zinc-400 mb-1">{label}</label>{children}</div>;
}
function Center({ children }) {
  return <div className="min-h-[100dvh] flex items-center justify-center bg-background">{children}</div>;
}
